import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Locale, setDefaultOptions } from "date-fns";
import { pl } from "date-fns/locale/pl";
import { useCallback, useEffect, useState } from "react";
import { IntlProvider } from "react-intl";

import { axiosInstance } from "api/setup";
import { getFromLocalStorage, setToLocalStorage } from "utils/localStorage";

import { SUPPORTED_LOCALES, DEFAULT_LOCALE } from "./localeConsts";
import { LocaleContext } from "./LocaleContext";
import enMessages from "../messages/en-US.json";
import plMessages from "../messages/pl-PL.json";

const LOCALE_KEY = "locale";

const getInitialLocale = (): string => {
  const stored = getFromLocalStorage<string>(LOCALE_KEY);
  if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored))
    return stored;

  const browserLang = navigator.language.split("-")[0];
  if ((SUPPORTED_LOCALES as readonly string[]).includes(browserLang))
    return browserLang;

  return DEFAULT_LOCALE;
};

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [locale, setLocaleState] = useState(getInitialLocale);
  const [dateFnsLocale, setDateFnsLocale] = useState<Locale>(pl);

  const syncLocale = useCallback((newLocale: string) => {
    setLocaleState(newLocale);
    setToLocalStorage(LOCALE_KEY, newLocale);
  }, []);

  const setLocale = useCallback(
    (newLocale: string) => {
      syncLocale(newLocale);
      axiosInstance.put("/user", { locale: newLocale }).catch(() => {});
    },
    [syncLocale],
  );

  const messages: Record<string, Record<string, string>> = {
    en: enMessages,
    pl: plMessages,
  };

  // Dynamically load the locale based on the selected language
  useEffect(() => {
    const loadLocale = async () => {
      if (locale === "pl") {
        const { pl } = await import("date-fns/locale/pl");
        setDateFnsLocale(pl);
        setDefaultOptions({ locale: pl });
      } else if (locale === "en") {
        const { enUS } = await import("date-fns/locale/en-US");
        setDateFnsLocale(enUS);
        setDefaultOptions({ locale: enUS });
      }
    };

    loadLocale();
  }, [locale]);

  return (
    <LocaleContext.Provider
      value={{ locale, setLocale, syncLocale, dateFnsLocale, setDateFnsLocale }}
    >
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={dateFnsLocale}
      >
        <IntlProvider locale={locale} messages={messages[locale]}>
          {children}
        </IntlProvider>
      </LocalizationProvider>
    </LocaleContext.Provider>
  );
};
